import {
  sendFee,
  type UnsignedView,
  type WalletActivityItem,
  type WalletResponse,
} from '@xchange/shared';
import { useEffect, useState } from 'react';
import { api, RequestError } from '../api.js';
import {
  ASSETS,
  amountText,
  assetName,
  bareText,
  onChain,
  parseAmount,
  type Asset,
} from '../assets.js';
import { normalizeDecimal } from '../numbers.js';
import type { Platform } from '../platform/index.js';
import { signUnsigned, type Signed } from '../sign.js';
import { toast } from '../toast.js';
import { markBusy } from '../update.js';
import { BuyUsdc, BuyUsdcButton } from './BuyUsdc.js';
import { Mark } from './Market.js';
import { PinPad } from './PinPad.js';

/**
 * The Balance tab: the three tokens, and Receive or Send for any of them.
 * Receive shows the address the token lives at. Send builds the transfer
 * here, signs it on the phone, and posts it; the fee on Solana is paid by
 * xchange, on Groot from the Gaju sent.
 */
type Step =
  | { kind: 'home' }
  | { kind: 'pick'; action: 'receive' | 'send' }
  | { kind: 'receive'; asset: Asset }
  | { kind: 'send'; asset: Asset }
  | { kind: 'history'; asset: Asset; items: WalletActivityItem[] | null }
  | { kind: 'pin'; unsigned: UnsignedView; then: (s: Signed) => Promise<void>; back: Step };

export function Balance({
  platform,
  wallet,
  reload,
}: {
  platform: Platform;
  wallet: WalletResponse | null;
  reload: () => Promise<void>;
}) {
  const [step, setStep] = useState<Step>({ kind: 'home' });
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendFees, setSendFees] = useState<Awaited<ReturnType<typeof api.sendFees>> | null>(null);
  useEffect(() => {
    // Without them the send screen still works; it just cannot quote the fee
    // before the PIN screen, which always shows it.
    api.sendFees().then(setSendFees, () => undefined);
  }, []);
  // A reload for a new release waits while something is in flight here.
  useEffect(() => {
    markBusy(busy);
    return () => {
      markBusy(false);
    };
  }, [busy]);
  const [picked, setPicked] = useState<string | null>(null);
  const b = wallet?.balances;
  const balance = (asset: Asset): string => {
    const v = b?.[asset] ?? null;
    return v === null ? '…' : bareText(asset, BigInt(v));
  };
  const addressOf = (asset: Asset): string | null =>
    (asset === 'GAJU' ? wallet?.addresses.gajumaru : wallet?.addresses.solana) ?? null;
  useEffect(() => {
    if (step.kind !== 'history' || step.items !== null) return;
    const asset = step.asset;
    void api
      .walletActivity(asset)
      .then((items) => {
        setStep((s) => (s.kind === 'history' && s.asset === asset ? { ...s, items } : s));
      })
      .catch(() => {
        setStep((s) => (s.kind === 'history' && s.asset === asset ? { ...s, items: [] } : s));
      });
  }, [step]);

  /** Copies and says so, the way this screen already does. */
  const copyHere = (text: string): void => {
    void navigator.clipboard.writeText(text).then(() => {
      toast.ok('Address copied');
    });
  };

  const fail = (e: unknown): void => {
    toast.err(
      e instanceof RequestError || e instanceof Error ? e.message : 'Something went wrong.',
    );
  };

  /** Cancel an open offer of yours from its history row: a refund you sign. */
  const cancelOffer = async (asset: Asset, offerId: string): Promise<void> => {
    setBusy(true);
    try {
      const { unsigned } = await api.cancelOffer(offerId);
      const back: Step = { kind: 'history', asset, items: null };
      if (unsigned === null) {
        // Nothing was in escrow, so there was nothing to sign.
        toast.ok('Cancelled. Nothing was in escrow.');
        setPicked(null);
        setStep(back);
        await reload();
        return;
      }
      setStep({
        kind: 'pin',
        unsigned,
        back,
        then: async (signed) => {
          await api.submitCancel(offerId, signed);
          toast.ok('Cancelled. Your funds are on their way back.');
          setPicked(null);
          setStep(back);
          await reload();
        },
      });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  /** The fee for the amount typed so far, in words, from the schedule the server gave. */
  const sendFeeLine = (asset: 'USDC' | 'XGE'): string => {
    const schedule = sendFees?.[asset] ?? null;
    if (schedule === null) return 'The network fee is paid by xchange.';
    let units = 0n;
    try {
      units = amount.trim() === '' ? 0n : parseAmount(asset, normalizeDecimal(amount));
    } catch {
      units = 0n;
    }
    const fee = sendFee(units, schedule);
    const extra = BigInt(schedule.newAccount);
    const rate = (schedule.bands[0]?.[1] ?? 0) / 100;
    if (rate === 0) return 'No xchange fee. The network fee is paid by xchange.';
    const lead =
      units > 0n
        ? `xchange fee: ${amountText(asset, fee)}, on top of the amount.`
        : `xchange fee: ${rate.toString()}%, less on large amounts, at least ${amountText(asset, BigInt(schedule.min))}.`;
    return `${lead}${extra > 0n ? ` ${amountText(asset, extra)} more if the address has never held ${assetName[asset]}, to open its account.` : ''} The network fee is paid by xchange, and so is opening the address's account if it has never held ${assetName[asset]}.`;
  };

  const send = async (asset: Asset): Promise<void> => {
    setBusy(true);
    try {
      const units = parseAmount(asset, normalizeDecimal(amount));
      const { unsigned, fee } = await api.sendPrepare({
        asset,
        to: to.trim(),
        amount: units.toString(),
      });
      setStep({
        kind: 'pin',
        unsigned,
        back: { kind: 'send', asset },
        then: async (signed) => {
          await api.sendSubmit(unsigned.chain, { ...signed, asset, amount: units.toString() });
          toast.ok(
            BigInt(fee) > 0n
              ? `${amountText(asset, units)} sent, fee ${amountText(asset, BigInt(fee))}`
              : `${amountText(asset, units)} sent`,
          );
          setTo('');
          setAmount('');
          setStep({ kind: 'home' });
          await reload();
        },
      });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === 'pin') {
    return (
      <PinPad
        title="Your PIN"
        hint={`${step.unsigned.summary}. Signed on this phone.`}
        busy={busy}
        onPin={(pin) => {
          setBusy(true);
          void signUnsigned(platform, pin, step.unsigned)
            .then(step.then)
            .catch((e: unknown) => {
              fail(e);
              if (!(e instanceof Error && e.message === 'Wrong PIN')) setStep(step.back);
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      />
    );
  }

  const header = (title: string, back: Step) => (
    <div className="header">
      <button
        className="ghost"
        onClick={() => {
          setStep(back);
        }}
      >
        ‹ Back
      </button>
      <h2 className="title">{title}</h2>
      <span style={{ width: 56 }} />
    </div>
  );

  if (step.kind === 'pick') {
    return (
      <div className="tab-screen">
        {header(step.action === 'receive' ? 'Receive' : 'Send', { kind: 'home' })}
        {step.action === 'receive' && (
          <BuyUsdcButton
            address={addressOf('USDC')}
            from="tg-receive"
            openLink={(url) => {
              platform.openLink(url);
            }}
            className="primary buy-usdc-cta"
            testId="tg-receive-buy-usdc"
          />
        )}
        {step.action === 'receive' && <p className="small">Or send a token you already hold.</p>}
        <div className="card" style={{ padding: '6px var(--pad)' }}>
          <div className="list">
            {ASSETS.map((asset) => (
              <button
                className="row"
                key={asset}
                onClick={() => {
                  setStep({ kind: step.action, asset });
                }}
                data-testid={`${step.action}-${asset}`}
              >
                <div className="mark-row">
                  <Mark asset={asset} size={26} />
                  <div>
                    <div className="desc" style={{ fontWeight: 500 }}>
                      {step.action === 'receive' ? onChain[asset] : assetName[asset]}
                    </div>
                    <div className="meta">{balance(asset)}</div>
                  </div>
                </div>
                <div className="amt" style={{ color: 'var(--hint)', fontWeight: 400 }}>
                  ›
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (step.kind === 'receive') {
    const a = addressOf(step.asset);
    return (
      <div className="tab-screen">
        {header(`Receive ${onChain[step.asset]}`, { kind: 'pick', action: 'receive' })}
        {step.asset === 'USDC' && (
          <BuyUsdc
            address={a}
            copy={copyHere}
            from="tg-receive-usdc"
            openLink={(url) => {
              platform.openLink(url);
            }}
          />
        )}
        <div className="card">
          <div className="label mark-row">
            <Mark asset={step.asset} /> Your {onChain[step.asset]} address
          </div>
          <div className="mono" style={{ wordBreak: 'break-all', marginTop: 8, fontSize: 15 }}>
            {a ?? '…'}
          </div>
        </div>
        <button
          className="primary"
          onClick={() => {
            if (a !== null) copyHere(a);
          }}
        >
          Copy address
        </button>
        {step.asset === 'USDC' && (
          <p className="small receive-network" data-testid="receive-network">
            USDC on the Solana network only. USDC sent on Ethereum, Base, Polygon, Tron or any other
            network is lost.
          </p>
        )}
        <p className="small" style={{ textAlign: 'center' }}>
          {step.asset === 'GAJU'
            ? 'Send Gaju here from GajuDesk or any Groot wallet.'
            : `Send ${assetName[step.asset]} here from any Solana wallet.`}{' '}
          It arrives in your own wallet, not at xchange.
        </p>
      </div>
    );
  }

  if (step.kind === 'history') {
    const asset = step.asset;
    return (
      <div className="tab-screen">
        {header(`${assetName[asset]} history`, { kind: 'home' })}
        <div className="card" style={{ padding: '0 var(--pad)' }}>
          <div className="row" style={{ cursor: 'default' }}>
            <div className="mark-row">
              <Mark asset={asset} size={26} />
              <div className="desc" style={{ fontWeight: 500 }}>
                {assetName[asset]}
              </div>
            </div>
            <div className="amt">{balance(asset)}</div>
          </div>
        </div>
        {step.items === null ? (
          <p className="hint">Loading.</p>
        ) : step.items.length === 0 ? (
          <div className="empty">Nothing yet for {assetName[asset]}.</div>
        ) : (
          <div className="list" data-testid="wallet-history">
            {step.items.map((it) => {
              const n = BigInt(it.amount);
              return (
                <button
                  key={it.id}
                  className="row"
                  onClick={() => {
                    if (it.cancellable !== undefined) setPicked(picked === it.id ? null : it.id);
                    else if (it.url !== null) platform.openLink(it.url);
                  }}
                >
                  <div>
                    <div className="desc">{it.text}</div>
                    <div className="meta">
                      {new Date(it.at).toLocaleString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {it.kind === 'reward'
                        ? ' · Reward · Tap to view on chain'
                        : it.cancellable !== undefined
                          ? ' · Open · Tap to cancel'
                          : it.url !== null
                            ? ' · Tap to view on chain'
                            : ''}
                    </div>
                    {it.cancellable !== undefined && picked === it.id && (
                      <span
                        className="ob-cue sell"
                        style={{ display: 'inline-block', marginTop: 8 }}
                        role="button"
                        data-testid="history-cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!busy) void cancelOffer(asset, it.cancellable ?? '');
                        }}
                      >
                        Cancel this offer
                      </span>
                    )}
                  </div>
                  <div className={`amt ${n > 0n ? 'pos' : n < 0n ? 'neg' : ''}`}>
                    {n === 0n ? '' : `${n > 0n ? '+' : '−'}${bareText(asset, n < 0n ? -n : n)}`}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (step.kind === 'send') {
    const asset = step.asset;
    return (
      <div className="tab-screen">
        {header(`Send ${assetName[asset]}`, { kind: 'pick', action: 'send' })}
        <label className="field">
          <div className="label">To</div>
          <input
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
            placeholder={asset === 'GAJU' ? 'ak_…' : 'Solana address'}
            style={{ fontSize: 16 }}
            data-testid="send-to"
          />
        </label>
        <label className="field">
          <div className="label">Amount (you have {balance(asset)})</div>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
            placeholder={asset === 'USDC' ? '$0.00' : `0 ${assetName[asset]}`}
            data-testid="send-amount"
          />
        </label>
        <p className="small" data-testid="send-fee">
          {asset === 'GAJU'
            ? 'No xchange fee. The network fee, 0.00002 Gaju, comes from your Gaju.'
            : sendFeeLine(asset)}
        </p>
        <div className="spacer" />
        <button
          className="primary"
          disabled={busy || to.trim() === '' || amount.trim() === ''}
          aria-busy={busy}
          onClick={() => void send(asset)}
          data-testid="send-submit"
        >
          Send {assetName[asset]}
        </button>
      </div>
    );
  }

  return (
    <div className="tab-screen">
      <div className="header">
        <h2 className="title">Balance</h2>
      </div>
      <div className="card" style={{ padding: '6px var(--pad)' }}>
        <div className="list">
          {ASSETS.map((asset) => (
            <button
              className="row"
              key={asset}
              onClick={() => {
                setStep({ kind: 'history', asset, items: null });
              }}
              data-testid={`self-${asset.toLowerCase()}`}
            >
              <div className="mark-row">
                <Mark asset={asset} size={26} />
                <div>
                  <div className="desc" style={{ fontWeight: 500 }}>
                    {asset === 'USDC' ? 'USDC (Solana)' : assetName[asset]}
                  </div>
                </div>
              </div>
              <div className="amt">{balance(asset)}</div>
            </button>
          ))}
        </div>
      </div>
      <BuyUsdcButton
        address={addressOf('USDC')}
        from="tg-balance"
        openLink={(url) => {
          platform.openLink(url);
        }}
        className="primary buy-usdc-cta"
        testId="tg-balance-buy-usdc"
      />
      <div className="actions">
        <button
          className="secondary"
          style={{ textAlign: 'center' }}
          onClick={() => {
            setStep({ kind: 'pick', action: 'receive' });
          }}
          data-testid="receive"
        >
          Receive
        </button>
        <button
          className="secondary"
          style={{ textAlign: 'center' }}
          onClick={() => {
            setStep({ kind: 'pick', action: 'send' });
          }}
          data-testid="send"
        >
          Send
        </button>
      </div>
      <p className="small" style={{ textAlign: 'center' }}>
        Tap a token for its history. Your own wallet, on this phone; xchange never holds your funds.
      </p>
    </div>
  );
}
